import { useState } from 'react';
import type { BreakdownSlice, ComparisonTrend, StackedTrend, TrendPoint } from '../../shared/contracts';
import { formatNumber } from '../../shared/format';

const DONUT_COLORS = ['#1a3e2f', '#2D7448', '#b8893a', '#2563EB', '#8a8474', '#d0393f'];

/** Stacked monthly bar chart (one colour per series) with a total line + legend. */
export function StackedBarChart({ data, onSelect }: { data: StackedTrend; onSelect?: (bucket: string) => void }) {
  const [hi, setHi] = useState<number | null>(null);
  const { months, series, total } = data;
  const max = Math.max(...total, 1);
  const n = months.length;
  const bw = 100 / n;
  // Same scale as the stacked rects so the line sits exactly on top of each stack
  // (a zero-total month lands on the baseline). Iterate months so a stray total can't overflow.
  const linePts = months.map((_, i) => ({ x: i * bw + bw / 2, y: 50 - ((total[i] ?? 0) / max) * 48 }));
  const line = linePts.map((p, i) => `${i === 0 ? 'M' : 'L'}${p.x.toFixed(1)},${p.y.toFixed(1)}`).join(' ');
  const color = (i: number) => DONUT_COLORS[i % DONUT_COLORS.length];

  return (
    <div className="relative">
      <svg viewBox="0 0 100 50" className="h-24 w-full" preserveAspectRatio="none">
        {months.map((m, i) => {
          let y = 50;
          return (
            <g key={m} className="cursor-pointer" onMouseEnter={() => setHi(i)} onMouseLeave={() => setHi(null)} onClick={() => onSelect?.(m)}>
              <rect x={i * bw} y="0" width={bw} height="50" fill="transparent" />
              {series.map((s, si) => {
                const h = ((s.values[i] ?? 0) / max) * 48;
                if (h <= 0) return null;
                y -= h;
                return (
                  <rect key={s.id} x={i * bw + bw * 0.12} y={y} width={bw * 0.76} height={h} fill={color(si)} style={{ opacity: hi === null || hi === i ? 1 : 0.4 }} />
                );
              })}
            </g>
          );
        })}
        <path d={line} fill="none" stroke="#1a3e2f" strokeWidth="0.8" strokeLinecap="round" strokeLinejoin="round" opacity="0.55" />
      </svg>
      <div className="mt-1 flex justify-between text-[9px] text-brand-dark/40">
        {months.map((m) => (
          <span key={m}>{m.slice(5)}</span>
        ))}
      </div>
      <ul className="mt-2 flex flex-wrap gap-x-3 gap-y-1 text-[10px]">
        {series.map((s, i) => (
          <li key={s.id} className="flex items-center gap-1.5">
            <span className="h-2 w-2 rounded-full" style={{ background: color(i) }} />
            <span className="text-brand-dark/60">{s.name}</span>
          </li>
        ))}
      </ul>
      {hi !== null && months[hi] && (
        <div
          className="pointer-events-none absolute -top-2 z-10 -translate-x-1/2 whitespace-nowrap rounded bg-brand-dark px-2 py-1 text-[10px] text-white"
          style={{ left: `${((hi + 0.5) / n) * 100}%` }}
        >
          {months[hi]}: {formatNumber(total[hi] ?? 0)}
        </div>
      )}
    </div>
  );
}

/** Interactive bar chart (hover tooltip + clickable bars). Renders via SVG rects. */
export function BarChart({ data, onSelect }: { data: TrendPoint[]; onSelect?: (bucket: string) => void }) {
  const [hi, setHi] = useState<number | null>(null);
  const max = Math.max(...data.map((d) => d.value), 1);
  const n = data.length;
  return (
    <div className="relative">
      <svg viewBox="0 0 100 50" className="h-24 w-full" preserveAspectRatio="none">
        {data.map((d, i) => {
          const h = Math.max(1.5, (d.value / max) * 48);
          const bw = 100 / n;
          const last = i === n - 1;
          const active = hi === i;
          return (
            <rect
              key={d.label}
              x={i * bw + bw * 0.12}
              y={50 - h}
              width={bw * 0.76}
              height={h}
              rx="0.8"
              className={`cursor-pointer transition-colors ${active ? 'fill-brand-accent' : last ? 'fill-brand-primary' : 'fill-brand-primary/25'}`}
              onMouseEnter={() => setHi(i)}
              onMouseLeave={() => setHi(null)}
              onClick={() => onSelect?.(d.label)}
            />
          );
        })}
      </svg>
      <div className="mt-1 flex justify-between text-[9px] text-brand-dark/40">
        {data.map((d) => (
          <span key={d.label}>{d.label.slice(5)}</span>
        ))}
      </div>
      {hi !== null && data[hi] && (
        <div
          className="pointer-events-none absolute -top-6 z-10 -translate-x-1/2 whitespace-nowrap rounded bg-brand-dark px-2 py-1 text-[10px] text-white"
          style={{ left: `${((hi + 0.5) / n) * 100}%` }}
        >
          {data[hi]!.label}: {formatNumber(data[hi]!.value)}
        </div>
      )}
    </div>
  );
}

/** Interactive area chart with hover points. */
export function AreaChart({ data, onSelect }: { data: TrendPoint[]; onSelect?: (bucket: string) => void }) {
  const [hi, setHi] = useState<number | null>(null);
  if (data.length < 2) return null;
  const w = 320;
  const h = 110;
  const pad = 6;
  const max = Math.max(...data.map((d) => d.value));
  const min = Math.min(...data.map((d) => d.value));
  const range = max - min || 1;
  const pts = data.map((d, i) => ({
    x: pad + (i / (data.length - 1)) * (w - pad * 2),
    y: h - pad - ((d.value - min) / range) * (h - pad * 2),
  }));
  const line = pts.map((p, i) => `${i === 0 ? 'M' : 'L'}${p.x.toFixed(1)},${p.y.toFixed(1)}`).join(' ');
  const area = `${line} L${pts[pts.length - 1]!.x},${h} L${pts[0]!.x},${h} Z`;
  const id = `area-${data.length}-${Math.round(max)}`;
  return (
    <div className="relative">
      <svg viewBox={`0 0 ${w} ${h}`} className="h-40 w-full" preserveAspectRatio="none" onMouseLeave={() => setHi(null)}>
        <defs>
          <linearGradient id={id} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#2D7448" stopOpacity="0.22" />
            <stop offset="100%" stopColor="#2D7448" stopOpacity="0" />
          </linearGradient>
        </defs>
        {[0.25, 0.5, 0.75].map((g) => (
          <line key={g} x1="0" y1={h * g} x2={w} y2={h * g} stroke="#1a3e2f" strokeOpacity="0.06" strokeWidth="1" />
        ))}
        <path d={area} fill={`url(#${id})`} />
        <path d={line} fill="none" stroke="#2D7448" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
        {pts.map((p, i) => (
          <g key={i}>
            <rect
              x={p.x - (w / data.length) / 2}
              y="0"
              width={w / data.length}
              height={h}
              fill="transparent"
              className="cursor-pointer"
              onMouseEnter={() => setHi(i)}
              onClick={() => onSelect?.(data[i]!.label)}
            />
            {hi === i && <circle cx={p.x} cy={p.y} r="4" className="fill-brand-primary" />}
          </g>
        ))}
      </svg>
      <div className="mt-1 flex justify-between text-[9px] text-brand-dark/40">
        {data.map((d) => (
          <span key={d.label}>{d.label.slice(5)}</span>
        ))}
      </div>
      {hi !== null && data[hi] && (
        <div className="pointer-events-none absolute -top-1 left-1/2 -translate-x-1/2 rounded bg-brand-dark px-2 py-1 text-[10px] text-white">
          {data[hi]!.label}: {formatNumber(data[hi]!.value)}
        </div>
      )}
    </div>
  );
}

/** Donut/pie chart with a legend; hover highlights, click selects a slice.
 *  Narrow cards stack the legend under the centered pie; `legendRight` (wide cards)
 *  puts it beside. Long legends scroll INSIDE their own area so the card keeps its
 *  height and the panel body never grows a scrollbar. */
export function DonutChart({ data, onSelect, legendRight }: { data: BreakdownSlice[]; onSelect?: (bucket: string) => void; legendRight?: boolean }) {
  const [hi, setHi] = useState<number | null>(null);
  const total = data.reduce((a, b) => a + b.value, 0) || 1;
  const r = 15.9155; // circumference ≈ 100
  const c = 2 * Math.PI * r;
  let acc = 0;
  return (
    // legendRight only takes effect from `sm` up — on a phone the card is one column
    // regardless of its nominal span, so the legend always stacks below there.
    <div className={legendRight ? 'flex h-full flex-col items-center justify-center gap-4 sm:flex-row sm:gap-6' : 'flex h-full flex-col items-center justify-center gap-4'}>
      <svg viewBox="0 0 40 40" className="h-36 w-36 shrink-0">
        <g transform="rotate(-90 20 20)">
          {data.map((s, i) => {
            const frac = s.value / total;
            const len = frac * c;
            const off = -acc * c;
            acc += frac;
            const active = hi === null || hi === i;
            return (
              <circle
                key={s.label}
                cx="20"
                cy="20"
                r={r}
                fill="none"
                strokeWidth={hi === i ? 9 : 7}
                stroke={DONUT_COLORS[i % DONUT_COLORS.length]}
                strokeDasharray={`${len} ${c - len}`}
                strokeDashoffset={off}
                className="cursor-pointer transition-all"
                style={{ opacity: active ? 1 : 0.35 }}
                onMouseEnter={() => setHi(i)}
                onMouseLeave={() => setHi(null)}
                onClick={() => onSelect?.(s.label)}
              />
            );
          })}
        </g>
      </svg>
      <ul className={`space-y-1 overflow-y-auto pr-1 text-xs ${legendRight ? 'max-h-36 w-full max-w-xs sm:max-h-40 sm:max-w-60' : 'max-h-36 w-full max-w-xs'}`}>
        {data.map((s, i) => (
          <li
            key={s.label}
            className="flex cursor-pointer items-center gap-2"
            onMouseEnter={() => setHi(i)}
            onMouseLeave={() => setHi(null)}
            onClick={() => onSelect?.(s.label)}
          >
            <span className="h-2.5 w-2.5 rounded-full" style={{ background: DONUT_COLORS[i % DONUT_COLORS.length] }} />
            <span className="text-brand-dark/70">{s.label}</span>
            <span className="ml-auto font-medium text-brand-dark">{Math.round((s.value / total) * 100)} %</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

/** Circular progress ring (rating or % to target). */
export function RadialRing({ value, max, centerLabel, sublabel, className = 'h-28 w-28' }: { value: number; max: number; centerLabel: string; sublabel?: string; className?: string }) {
  const pct = Math.max(0, Math.min(1, max === 0 ? 0 : value / max));
  const r = 42;
  const circumference = 2 * Math.PI * r;
  return (
    <svg viewBox="0 0 100 100" className={`${className} shrink-0`}>
      <circle cx="50" cy="50" r={r} fill="none" stroke="#1a3e2f" strokeOpacity="0.1" strokeWidth="9" />
      <circle cx="50" cy="50" r={r} fill="none" stroke="#2D7448" strokeWidth="9" strokeLinecap="round" strokeDasharray={circumference} strokeDashoffset={circumference * (1 - pct)} transform="rotate(-90 50 50)" style={{ transition: 'stroke-dashoffset 0.6s ease' }} />
      <text x="50" y="49" textAnchor="middle" className="fill-brand-primary font-heading" fontSize="22" fontWeight="700">{centerLabel}</text>
      {sublabel && <text x="50" y="64" textAnchor="middle" className="fill-brand-dark/45" fontSize="9">{sublabel}</text>}
    </svg>
  );
}

/**
 * Cumulative actual-vs-target comparison ("din udvikling mod mål"): the target as a
 * dashed guide line across the whole window, the actual as a filled area up to the
 * current month. Scales share one axis so the gap IS the distance to target.
 */
export function ComparisonChart({ data }: { data: ComparisonTrend }) {
  const { months, actual, target } = data;
  if (months.length < 2 || target.length < 2) return null;
  const w = 320;
  const h = 110;
  const pad = 6;
  const max = Math.max(...target, ...actual, 1);
  const x = (i: number) => pad + (i / (months.length - 1)) * (w - pad * 2);
  const y = (v: number) => h - pad - (v / max) * (h - pad * 2);
  const path = (values: number[]) =>
    values.map((v, i) => `${i === 0 ? 'M' : 'L'}${x(i).toFixed(1)},${y(v).toFixed(1)}`).join(' ');
  const targetLine = path(target.slice(0, months.length));
  const actualValues = actual.slice(0, months.length);
  const actualLine = path(actualValues);
  const area = actualValues.length >= 2 ? `${actualLine} L${x(actualValues.length - 1).toFixed(1)},${h} L${x(0).toFixed(1)},${h} Z` : '';
  return (
    <div className="mb-3 rounded-lg bg-brand-primary/[0.03] p-3">
      <div className="mb-1 flex items-center justify-between">
        <span className="text-xs font-medium text-brand-dark/70">{data.label}</span>
        <span className="flex items-center gap-3 text-[10px] text-brand-dark/50">
          <span className="flex items-center gap-1">
            <span className="inline-block h-0.5 w-4 bg-brand-accent" /> Realiseret
          </span>
          <span className="flex items-center gap-1">
            <span className="inline-block h-0 w-4 border-t-2 border-dashed border-brand-dark/35" /> Mål
          </span>
        </span>
      </div>
      <svg viewBox={`0 0 ${w} ${h}`} className="h-28 w-full" preserveAspectRatio="none" role="img" aria-label={data.label}>
        {[0.25, 0.5, 0.75].map((g) => (
          <line key={g} x1="0" y1={h * g} x2={w} y2={h * g} stroke="#1a3e2f" strokeOpacity="0.06" strokeWidth="1" />
        ))}
        {area && <path d={area} fill="#2D7448" fillOpacity="0.12" />}
        <path d={targetLine} fill="none" stroke="#1a3e2f" strokeOpacity="0.35" strokeWidth="2" strokeDasharray="5 4" />
        {actualValues.length >= 2 && (
          <path d={actualLine} fill="none" stroke="#2D7448" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
        )}
        {actualValues.length >= 1 && (
          // Always mark the latest point — a January-only series is a dot, not invisible.
          <circle cx={x(actualValues.length - 1)} cy={y(actualValues[actualValues.length - 1] ?? 0)} r="3" fill="#2D7448" />
        )}
      </svg>
      <div className="flex justify-between text-[9px] text-brand-dark/40">
        {months.map((m) => (
          <span key={m}>{m.slice(0, 3)}</span>
        ))}
      </div>
    </div>
  );
}
